/**
 * Formül alanı ifade motoru (WP 2.3, ADR-0004): sandbox — `eval` yok, kaynak erişimi yok, sınırlı adım/derinlik.
 * Sözdizimi: sayı, "metin", true/false/null, alan anahtarı (tanımlayıcı), + - * / % ^, karşılaştırma (= == != <> < <= > >=),
 * AND OR NOT, parantez, fonksiyon çağrısı. Sonuç türü çağıranın beklediği türe `coerceResult` ile indirgenir.
 * Hata sınıfı FormulaError (code: syntax | limit | type | unknown_function | unknown_field | division).
 */
export class FormulaError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.name = 'FormulaError';
        this.code = code;
    }
}
export const FORMULA_LIMITS = {
    maxLength: 2000,
    maxDepth: 32,
    maxNodes: 500,
    maxSteps: 20_000,
    maxStringLength: 10_000,
};
const IDENT_RE = /^[A-Za-z_][A-Za-z0-9_]*/;
function tokenize(src) {
    if (src.length > FORMULA_LIMITS.maxLength)
        throw new FormulaError('limit', 'İfade çok uzun');
    const out = [];
    let i = 0;
    while (i < src.length) {
        const c = src[i] ?? '';
        if (c === ' ' || c === '\t' || c === '\n' || c === '\r') {
            i += 1;
            continue;
        }
        if (c >= '0' && c <= '9') {
            const m = /^\d+(\.\d+)?([eE][+-]?\d+)?/.exec(src.slice(i));
            if (!m)
                throw new FormulaError('syntax', 'Geçersiz sayı');
            out.push({ t: 'num', v: Number(m[0]), raw: m[0] });
            i += m[0].length;
            continue;
        }
        if (c === '"' || c === "'") {
            let j = i + 1;
            let s = '';
            while (j < src.length && src[j] !== c) {
                if (src[j] === '\\' && j + 1 < src.length) {
                    s += src.charAt(j + 1);
                    j += 2;
                }
                else {
                    s += src.charAt(j);
                    j += 1;
                }
            }
            if (j >= src.length)
                throw new FormulaError('syntax', 'Kapanmamış metin');
            out.push({ t: 'str', v: s });
            i = j + 1;
            continue;
        }
        const id = IDENT_RE.exec(src.slice(i));
        if (id) {
            out.push({ t: 'id', v: id[0] });
            i += id[0].length;
            continue;
        }
        const two = src.slice(i, i + 2);
        if (['<=', '>=', '!=', '<>', '=='].includes(two)) {
            out.push({ t: 'op', v: two === '<>' ? '!=' : two === '==' ? '=' : two });
            i += 2;
            continue;
        }
        if ('+-*/%^<>='.includes(c)) {
            out.push({ t: 'op', v: c });
            i += 1;
            continue;
        }
        if (c === '(') {
            out.push({ t: 'lp' });
            i += 1;
            continue;
        }
        if (c === ')') {
            out.push({ t: 'rp' });
            i += 1;
            continue;
        }
        if (c === ',' || c === ';') {
            out.push({ t: 'comma' });
            i += 1;
            continue;
        }
        throw new FormulaError('syntax', `Beklenmeyen karakter: ${c}`);
    }
    out.push({ t: 'end' });
    return out;
}
class Parser {
    toks;
    pos = 0;
    nodes = 0;
    constructor(toks) {
        this.toks = toks;
    }
    peek() {
        return this.toks[this.pos] ?? { t: 'end' };
    }
    next() {
        const t = this.peek();
        this.pos += 1;
        return t;
    }
    node(n) {
        this.nodes += 1;
        if (this.nodes > FORMULA_LIMITS.maxNodes)
            throw new FormulaError('limit', 'İfade çok karmaşık');
        return n;
    }
    parse() {
        const e = this.expr(0, 0);
        if (this.peek().t !== 'end')
            throw new FormulaError('syntax', 'Fazla girdi');
        return e;
    }
    static prec(op) {
        switch (op) {
            case 'OR':
                return 1;
            case 'AND':
                return 2;
            case '=':
            case '!=':
            case '<':
            case '<=':
            case '>':
            case '>=':
                return 3;
            case '+':
            case '-':
                return 4;
            case '*':
            case '/':
            case '%':
                return 5;
            case '^':
                return 6;
            default:
                return 0;
        }
    }
    expr(minPrec, depth) {
        if (depth > FORMULA_LIMITS.maxDepth)
            throw new FormulaError('limit', 'İfade çok derin');
        let left = this.unary(depth);
        for (;;) {
            const t = this.peek();
            let op = null;
            if (t.t === 'op')
                op = t.v;
            else if (t.t === 'id' && (t.v.toUpperCase() === 'AND' || t.v.toUpperCase() === 'OR'))
                op = t.v.toUpperCase();
            if (op === null)
                break;
            const p = Parser.prec(op);
            if (p === 0 || p < minPrec)
                break;
            this.next();
            const right = this.expr(op === '^' ? p : p + 1, depth + 1);
            left = this.node({ k: 'bin', op, a: left, b: right });
        }
        return left;
    }
    unary(depth) {
        const t = this.peek();
        if (t.t === 'op' && t.v === '-') {
            this.next();
            return this.node({ k: 'un', op: 'neg', a: this.unary(depth + 1) });
        }
        if (t.t === 'op' && t.v === '+') {
            this.next();
            return this.unary(depth + 1);
        }
        if (t.t === 'id' && t.v.toUpperCase() === 'NOT') {
            this.next();
            return this.node({ k: 'un', op: 'not', a: this.unary(depth + 1) });
        }
        return this.primary(depth);
    }
    primary(depth) {
        const t = this.next();
        if (t.t === 'num')
            return this.node({ k: 'lit', v: t.v, raw: t.raw });
        if (t.t === 'str')
            return this.node({ k: 'lit', v: t.v });
        if (t.t === 'lp') {
            const e = this.expr(0, depth + 1);
            if (this.next().t !== 'rp')
                throw new FormulaError('syntax', 'Parantez kapanmadı');
            return e;
        }
        if (t.t === 'id') {
            const up = t.v.toUpperCase();
            if (up === 'TRUE')
                return this.node({ k: 'lit', v: true });
            if (up === 'FALSE')
                return this.node({ k: 'lit', v: false });
            if (up === 'NULL')
                return this.node({ k: 'lit', v: null });
            if (this.peek().t === 'lp') {
                this.next();
                const args = [];
                if (this.peek().t !== 'rp') {
                    for (;;) {
                        args.push(this.expr(0, depth + 1));
                        const n = this.next();
                        if (n.t === 'rp')
                            break;
                        if (n.t !== 'comma')
                            throw new FormulaError('syntax', 'Virgül bekleniyor');
                    }
                }
                else
                    this.next();
                return this.node({ k: 'call', fn: up, args });
            }
            return this.node({ k: 'field', name: t.v });
        }
        throw new FormulaError('syntax', 'Beklenmeyen ifade');
    }
}
export function parseFormula(src) {
    return new Parser(tokenize(src)).parse();
}
/** İfadede geçen alan anahtarları (bağımlılık analizi; formül alanı kendi kendine referans veremez). */
export function formulaFields(ast, out = new Set()) {
    switch (ast.k) {
        case 'field':
            out.add(ast.name);
            break;
        case 'un':
            formulaFields(ast.a, out);
            break;
        case 'bin':
            formulaFields(ast.a, out);
            formulaFields(ast.b, out);
            break;
        case 'call':
            for (const a of ast.args)
                formulaFields(a, out);
            break;
        default:
    }
    return out;
}
const DAY_MS = 86_400_000;
function toNum(v, what) {
    if (typeof v === 'number')
        return v;
    if (typeof v === 'boolean')
        return v ? 1 : 0;
    if (v === null)
        return 0;
    const n = Number(v);
    if (v.trim() === '' || Number.isNaN(n))
        throw new FormulaError('type', `${what}: sayı bekleniyor`);
    return n;
}
function toStr(v) {
    if (v === null)
        return '';
    if (typeof v === 'number')
        return Number.isInteger(v) ? String(v) : String(v);
    return typeof v === 'boolean' ? (v ? 'true' : 'false') : v;
}
function toBool(v) {
    if (v === null)
        return false;
    if (typeof v === 'boolean')
        return v;
    if (typeof v === 'number')
        return v !== 0;
    return v !== '' && v.toLowerCase() !== 'false';
}
function toDate(v, what) {
    if (typeof v === 'number')
        return new Date(v);
    if (typeof v !== 'string' || v === '')
        throw new FormulaError('type', `${what}: tarih bekleniyor`);
    const d = new Date(v.length === 10 ? `${v}T00:00:00Z` : v);
    if (Number.isNaN(d.getTime()))
        throw new FormulaError('type', `${what}: geçersiz tarih`);
    return d;
}
function isoDate(d) {
    return d.toISOString().slice(0, 10);
}
function cmp(a, b) {
    if (typeof a === 'number' || typeof b === 'number')
        return toNum(a, 'karşılaştırma') - toNum(b, 'karşılaştırma');
    if (typeof a === 'boolean' || typeof b === 'boolean')
        return Number(toBool(a)) - Number(toBool(b));
    const sa = toStr(a);
    const sb = toStr(b);
    return sa < sb ? -1 : sa > sb ? 1 : 0;
}
function limitStr(s) {
    if (s.length > FORMULA_LIMITS.maxStringLength)
        throw new FormulaError('limit', 'Metin çok uzun');
    return s;
}
function scalar(v) {
    if (v === null || v === undefined)
        return null;
    if (typeof v === 'number')
        return Number.isFinite(v) ? v : null;
    if (typeof v === 'string' || typeof v === 'boolean')
        return v;
    if (typeof v === 'object' && 'amount' in v) {
        const a = v.amount;
        return typeof a === 'number' ? a : null;
    }
    return null;
}
export function evaluateFormula(ast, ctx) {
    let steps = 0;
    const now = ctx.now ?? new Date();
    const step = () => {
        steps += 1;
        if (steps > FORMULA_LIMITS.maxSteps)
            throw new FormulaError('limit', 'Adım sınırı aşıldı');
    };
    const ev = (n) => {
        step();
        switch (n.k) {
            case 'lit':
                return n.v;
            case 'field': {
                if (!Object.hasOwn(ctx.fields, n.name))
                    throw new FormulaError('unknown_field', `Bilinmeyen alan: ${n.name}`);
                return scalar(ctx.fields[n.name]);
            }
            case 'un': {
                const a = ev(n.a);
                return n.op === 'neg' ? -toNum(a, 'işaret') : !toBool(a);
            }
            case 'bin': {
                if (n.op === 'AND')
                    return toBool(ev(n.a)) ? toBool(ev(n.b)) : false;
                if (n.op === 'OR')
                    return toBool(ev(n.a)) ? true : toBool(ev(n.b));
                const a = ev(n.a);
                const b = ev(n.b);
                switch (n.op) {
                    case '+':
                        if (typeof a === 'string' || typeof b === 'string')
                            return limitStr(toStr(a) + toStr(b));
                        return toNum(a, '+') + toNum(b, '+');
                    case '-':
                        return toNum(a, '-') - toNum(b, '-');
                    case '*':
                        return toNum(a, '*') * toNum(b, '*');
                    case '/': {
                        const d = toNum(b, '/');
                        if (d === 0)
                            throw new FormulaError('division', 'Sıfıra bölme');
                        return toNum(a, '/') / d;
                    }
                    case '%': {
                        const d = toNum(b, '%');
                        if (d === 0)
                            throw new FormulaError('division', 'Sıfıra bölme');
                        return toNum(a, '%') % d;
                    }
                    case '^': {
                        const r = Math.pow(toNum(a, '^'), toNum(b, '^'));
                        if (!Number.isFinite(r))
                            throw new FormulaError('limit', 'Sayısal taşma');
                        return r;
                    }
                    case '=':
                        return cmp(a, b) === 0;
                    case '!=':
                        return cmp(a, b) !== 0;
                    case '<':
                        return cmp(a, b) < 0;
                    case '<=':
                        return cmp(a, b) <= 0;
                    case '>':
                        return cmp(a, b) > 0;
                    case '>=':
                        return cmp(a, b) >= 0;
                    default:
                        throw new FormulaError('syntax', `Bilinmeyen işleç: ${n.op}`);
                }
            }
            case 'call':
                return call(n.fn, n.args);
            default:
                throw new FormulaError('syntax', 'Geçersiz düğüm');
        }
    };
    const A = (args, i) => {
        const x = args[i];
        if (!x)
            throw new FormulaError('syntax', 'Eksik argüman');
        return x;
    };
    const need = (fn, args, min, max = min) => {
        if (args.length < min || args.length > max)
            throw new FormulaError('syntax', `${fn}: argüman sayısı ${min}${max !== min ? `–${max}` : ''} olmalı`);
    };
    const call = (fn, args) => {
        switch (fn) {
            case 'IF': {
                need(fn, args, 2, 3);
                const c = toBool(ev(A(args, 0)));
                return c ? ev(A(args, 1)) : args[2] ? ev(args[2]) : null;
            }
            case 'COALESCE': {
                need(fn, args, 1, 20);
                for (const a of args) {
                    const v = ev(a);
                    if (v !== null && v !== '')
                        return v;
                }
                return null;
            }
            case 'ISBLANK':
                need(fn, args, 1);
                return (() => {
                    const v = ev(A(args, 0));
                    return v === null || v === '';
                })();
            case 'ROUND': {
                need(fn, args, 1, 2);
                const d = args[1] ? Math.min(10, Math.max(0, Math.trunc(toNum(ev(args[1]), 'ROUND')))) : 0;
                const f = 10 ** d;
                return Math.round((toNum(ev(A(args, 0)), 'ROUND') + Number.EPSILON) * f) / f;
            }
            case 'FLOOR':
                need(fn, args, 1);
                return Math.floor(toNum(ev(A(args, 0)), fn));
            case 'CEIL':
                need(fn, args, 1);
                return Math.ceil(toNum(ev(A(args, 0)), fn));
            case 'ABS':
                need(fn, args, 1);
                return Math.abs(toNum(ev(A(args, 0)), fn));
            case 'MIN':
            case 'MAX':
            case 'SUM':
            case 'AVG': {
                need(fn, args, 1, 50);
                const nums = args
                    .map((a) => ev(a))
                    .filter((v) => v !== null && v !== '')
                    .map((v) => toNum(v, fn));
                if (nums.length === 0)
                    return null;
                if (fn === 'MIN')
                    return Math.min(...nums);
                if (fn === 'MAX')
                    return Math.max(...nums);
                const sum = nums.reduce((x, y) => x + y, 0);
                return fn === 'SUM' ? sum : sum / nums.length;
            }
            case 'NUMBER':
                need(fn, args, 1);
                return (() => {
                    const v = ev(A(args, 0));
                    return v === null || v === '' ? null : toNum(v, fn);
                })();
            case 'TEXT':
                need(fn, args, 1);
                return toStr(ev(A(args, 0)));
            case 'LEN':
                need(fn, args, 1);
                return toStr(ev(A(args, 0))).length;
            case 'CONCAT':
                need(fn, args, 1, 50);
                return limitStr(args.map((a) => toStr(ev(a))).join(''));
            case 'UPPER':
                need(fn, args, 1);
                return toStr(ev(A(args, 0))).toLocaleUpperCase('tr-TR');
            case 'LOWER':
                need(fn, args, 1);
                return toStr(ev(A(args, 0))).toLocaleLowerCase('tr-TR');
            case 'TRIM':
                need(fn, args, 1);
                return toStr(ev(A(args, 0))).trim();
            case 'LEFT':
            case 'RIGHT': {
                need(fn, args, 2);
                const s = toStr(ev(A(args, 0)));
                const n = Math.max(0, Math.trunc(toNum(ev(A(args, 1)), fn)));
                return fn === 'LEFT' ? s.slice(0, n) : s.slice(Math.max(0, s.length - n));
            }
            case 'CONTAINS':
                need(fn, args, 2);
                return toStr(ev(A(args, 0)))
                    .toLocaleLowerCase('tr-TR')
                    .includes(toStr(ev(A(args, 1))).toLocaleLowerCase('tr-TR'));
            case 'TODAY':
                need(fn, args, 0);
                return isoDate(now);
            case 'NOW':
                need(fn, args, 0);
                return now.toISOString();
            case 'YEAR':
            case 'MONTH':
            case 'DAY': {
                need(fn, args, 1);
                const d = toDate(ev(A(args, 0)), fn);
                return fn === 'YEAR'
                    ? d.getUTCFullYear()
                    : fn === 'MONTH'
                        ? d.getUTCMonth() + 1
                        : d.getUTCDate();
            }
            case 'DAYS_BETWEEN': {
                need(fn, args, 2);
                const a = toDate(ev(A(args, 0)), fn);
                const b = toDate(ev(A(args, 1)), fn);
                return Math.round((b.getTime() - a.getTime()) / DAY_MS);
            }
            case 'DATE_ADD': {
                need(fn, args, 2, 3);
                const d = toDate(ev(A(args, 0)), fn);
                const n = Math.trunc(toNum(ev(A(args, 1)), fn));
                const unit = args[2] ? toStr(ev(args[2])).toLowerCase() : 'day';
                if (Math.abs(n) > 100_000)
                    throw new FormulaError('limit', 'DATE_ADD aralığı çok büyük');
                if (unit === 'day')
                    d.setUTCDate(d.getUTCDate() + n);
                else if (unit === 'month')
                    d.setUTCMonth(d.getUTCMonth() + n);
                else if (unit === 'year')
                    d.setUTCFullYear(d.getUTCFullYear() + n);
                else
                    throw new FormulaError('type', 'DATE_ADD birimi day|month|year olmalı');
                return isoDate(d);
            }
            default:
                throw new FormulaError('unknown_function', `Bilinmeyen fonksiyon: ${fn}`);
        }
    };
    return ev(ast);
}
/** Sonucu tanımlı tipe indirger; uyumsuzsa null (formül alanı boş kalır, hata yayılmaz). */
export function coerceResult(v, type) {
    if (v === null)
        return null;
    switch (type) {
        case 'number': {
            if (typeof v === 'number')
                return Number.isFinite(v) ? v : null;
            const n = Number(typeof v === 'boolean' ? (v ? 1 : 0) : v);
            return Number.isFinite(n) && String(v).trim() !== '' ? n : null;
        }
        case 'text':
            return limitStr(toStr(v));
        case 'boolean':
            return toBool(v);
        case 'date': {
            try {
                return isoDate(toDate(v, 'sonuç'));
            }
            catch {
                return null;
            }
        }
        default:
            return null;
    }
}
/** Tek çağrı: parse + evaluate + coerce. Hata → { ok: false, error }. */
export function runFormula(src, ctx, resultType) {
    try {
        const ast = parseFormula(src);
        return { ok: true, value: coerceResult(evaluateFormula(ast, ctx), resultType) };
    }
    catch (e) {
        if (e instanceof FormulaError)
            return { ok: false, error: e };
        return { ok: false, error: new FormulaError('syntax', 'Formül değerlendirilemedi') };
    }
}
//# sourceMappingURL=formula.js.map